import { DeliveryRoute, UnassignedPicking } from "app/types/route";
import {
  MapDeliveryJob,
  MapDeliveryJobStatus,
  MapScreenDriver,
  MapTimelineDriverRow,
} from "app/types/mapScreen";
import { DriverOption } from "app/types/driver";
import { odooApiFetch } from "app/utils/auth";
import { extractOdooResults, serializeOdooFilters } from "app/utils/odooApi";
import { getRelationName } from "app/utils/odooDisplay";
import { toLatLngTuple } from "app/utils/routeMapGeo";

type MapStopPayload = {
  id: number;
  sequence?: number;
  lat?: number | false;
  lng?: number | false;
  name?: string;
  picking_name?: string;
  address?: string;
  status?: string;
};

type MapDataPayload = {
  stops?: MapStopPayload[];
};

type PartnerGeoRecord = {
  id: number;
  name?: string;
  geo_lat?: number | false;
  geo_lng?: number | false;
  street?: string | false;
  street2?: string | false;
  city?: string | false;
  zip?: string | false;
};

function formatPartnerAddress(partner?: PartnerGeoRecord): string {
  if (!partner) {
    return "";
  }
  const parts = [partner.street, partner.street2, partner.city, partner.zip]
    .filter((part) => typeof part === "string" && part.trim() !== "")
    .map((part) => String(part).trim());
  return parts.join(", ");
}

function getApiRoot(): string {
  return (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
}

function mapStopStatus(status?: string, routeState?: string): MapDeliveryJobStatus {
  if (status === "delivered") {
    return "delivered";
  }
  if (status === "failed" || status === "not_delivered") {
    return "failed";
  }
  if (routeState === "in_progress" || routeState === "loaded") {
    return "in_route";
  }
  return "pending";
}

async function fetchPartnersWithGeo(
  partnerIds: number[],
  accessToken: string
): Promise<Map<number, PartnerGeoRecord>> {
  if (partnerIds.length === 0) {
    return new Map();
  }

  const filters = [["id", "in", partnerIds]];
  const parsed = await odooApiFetch<{ results?: PartnerGeoRecord[] }>(
    `${getApiRoot()}/res.partner/?filters=${encodeURIComponent(
      serializeOdooFilters(filters)
    )}&include_fields=${encodeURIComponent(
      "['id','name','geo_lat','geo_lng','street','street2','city','zip']"
    )}&limit=${partnerIds.length}`,
    accessToken
  );

  const map = new Map<number, PartnerGeoRecord>();
  for (const partner of extractOdooResults<PartnerGeoRecord>(parsed)) {
    map.set(partner.id, partner);
  }
  return map;
}

export async function fetchRouteMapJobs(
  routes: DeliveryRoute[],
  accessToken: string
): Promise<MapDeliveryJob[]> {
  const jobs: MapDeliveryJob[] = [];
  const responses = await Promise.all(
    routes.map(async (route) => {
      try {
        const payload = await odooApiFetch<MapDataPayload>(
          `${getApiRoot()}/route/${route.id}/map_data`,
          accessToken
        );
        return { route, payload };
      } catch {
        return { route, payload: null };
      }
    })
  );

  for (const { route, payload } of responses) {
    if (!payload?.stops) {
      continue;
    }
    for (const stop of payload.stops) {
      const point = toLatLngTuple(stop.lat, stop.lng);
      if (!point) {
        continue;
      }
      jobs.push({
        id: `stop-${stop.id}`,
        stopId: stop.id,
        routeId: route.id,
        routeName: route.name,
        name: stop.picking_name || stop.name || `Stop ${stop.sequence ?? ""}`.trim(),
        pickingNumber: stop.picking_name || undefined,
        partnerName: stop.name,
        address: stop.address || "",
        customerName: stop.name,
        lat: point[0],
        lng: point[1],
        status: mapStopStatus(stop.status, route.state),
        sequence: stop.sequence,
        scheduledDate: route.date,
      });
    }
  }

  return jobs;
}

export async function fetchUnassignedMapJobs(
  orders: UnassignedPicking[],
  accessToken: string
): Promise<MapDeliveryJob[]> {
  const partnerIds = Array.from(
    new Set(
      orders
        .map((order) =>
          typeof order.partner_id === "object" ? order.partner_id?.id : undefined
        )
        .filter((id): id is number => typeof id === "number")
    )
  );

  const partnerMap = await fetchPartnersWithGeo(partnerIds, accessToken);
  const jobs: MapDeliveryJob[] = [];

  for (const order of orders) {
    const partnerId =
      typeof order.partner_id === "object" ? order.partner_id?.id : undefined;
    const partner = partnerId ? partnerMap.get(partnerId) : undefined;
    const point = toLatLngTuple(partner?.geo_lat, partner?.geo_lng);
    if (!point) {
      continue;
    }

    jobs.push({
      id: `unassigned-${order.id}`,
      pickingId: order.id,
      name: order.name || `Order ${order.id}`,
      pickingNumber: order.name,
      partnerName: getRelationName(order.partner_id),
      address: formatPartnerAddress(partner),
      customerName: getRelationName(order.partner_id),
      lat: point[0],
      lng: point[1],
      status: "unassigned",
      scheduledDate: order.scheduled_date,
      loads: order.shipment_packages_count ?? 0,
    });
  }

  return jobs;
}

export function buildLiveDrivers(
  drivers: DriverOption[],
  routes: DeliveryRoute[]
): MapScreenDriver[] {
  const routeByDriver = new Map<number, DeliveryRoute>();
  for (const route of routes) {
    for (const line of route.driver_line_ids || []) {
      const driverId =
        typeof line.partner_id === "object" ? line.partner_id?.id : undefined;
      if (driverId) {
        routeByDriver.set(driverId, route);
      }
    }
  }

  const liveDrivers: MapScreenDriver[] = [];
  for (const driver of drivers) {
    const point = toLatLngTuple(driver.last_lat, driver.last_lng);
    if (!point) {
      continue;
    }
    const route = routeByDriver.get(driver.id);
    liveDrivers.push({
      id: driver.id,
      name: driver.name,
      lat: point[0],
      lng: point[1],
      isOnDuty: driver.is_on_duty,
      routeId: route?.id,
      routeName: route?.name,
      vehicleType: "Car",
      jobCount: route?.picking_count ?? 0,
    });
  }
  return liveDrivers;
}

export function buildTimelineRows(routes: DeliveryRoute[]): MapTimelineDriverRow[] {
  const rows: MapTimelineDriverRow[] = [];

  for (const route of routes) {
    const driverLines = route.driver_line_ids || [];
    const stops = [...(route.stop_line_ids || [])].sort(
      (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
    );

    if (driverLines.length === 0) {
      continue;
    }

    for (const line of driverLines) {
      const driverId =
        typeof line.partner_id === "object" ? line.partner_id?.id : undefined;
      const driverName = getRelationName(line.partner_id, "Driver");
      if (!driverId) {
        continue;
      }

      const startHour = parseRouteHour(route.start_time, 8);
      const endHour = Math.max(parseRouteHour(route.finish_time, 18), startHour + 4);

      rows.push({
        id: driverId,
        name: driverName,
        vehicleType:
          typeof line.vehicle_id === "object"
            ? line.vehicle_id?.vehicle_type || "Car"
            : "Car",
        routeId: route.id,
        routeName: route.name,
        jobCount: stops.length,
        hasAlert: route.state === "in_progress" && stops.length > 0,
        startHour,
        endHour,
        stops: stops.map((stop, index) => ({
          id: `stop-${stop.id}`,
          label: String(stop.sequence ?? index + 1),
          sequence: stop.sequence ?? index + 1,
          routeId: route.id,
          routeName: route.name,
        })),
      });
    }
  }

  return rows.sort((a, b) => a.name.localeCompare(b.name));
}

function parseRouteHour(value: number | undefined, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }
  const hours = Math.floor(value);
  return Math.min(Math.max(hours, 0), 23);
}

export async function loadMapScreenData(
  accessToken: string,
  routes: DeliveryRoute[],
  unassignedOrders: UnassignedPicking[],
  drivers: DriverOption[]
): Promise<{
  jobs: MapDeliveryJob[];
  liveDrivers: MapScreenDriver[];
  timelineRows: MapTimelineDriverRow[];
}> {
  const [routeJobs, unassignedJobs] = await Promise.all([
    fetchRouteMapJobs(routes, accessToken),
    fetchUnassignedMapJobs(unassignedOrders, accessToken),
  ]);

  return {
    jobs: [...routeJobs, ...unassignedJobs],
    liveDrivers: buildLiveDrivers(drivers, routes),
    timelineRows: buildTimelineRows(routes),
  };
}
